import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';

const Container = styled.div`
  padding: 2rem;
  max-width: 1100px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const BackLink = styled(Link)`
  color: #005eb8;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const Button = styled.button`
  padding: 8px 16px;
  background-color: #005eb8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  &:hover { background-color: #004a94; }
  &:disabled { background-color: #99b9d6; cursor: not-allowed; }
  &.secondary { background: #fff; color: #005eb8; border: 1px solid #005eb8; }
  &.secondary:hover { background: #f0f6fc; }
  &.danger { background: #fff; color: #d9534f; border: 1px solid #d9534f; }
  &.danger:hover { background: #d9534f; color: #fff; }
`;

const CategorySection = styled.div`
  margin-bottom: 2.5rem;
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #eee;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
`;

const CategoryTitle = styled.h2`
  margin: 0;
  font-size: 1.3rem;
  color: #333;
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 1rem;
`;

const ServiceCard = styled.div`
  border: 1px solid #e0e4e8;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  opacity: ${p => (p.$inactive ? 0.6 : 1)};
`;

const Thumb = styled.div`
  width: 100%;
  height: 110px;
  background: #f4f6f8 url(${p => p.$src || ''}) center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aab2ba;
  font-size: 0.8rem;
`;

const CardBody = styled.div`
  padding: 0.75rem 0.9rem;
`;

const CardTitle = styled.div`
  font-weight: 600;
  font-size: 0.92rem;
  margin-bottom: 0.3rem;
  line-height: 1.3;
`;

const CardMeta = styled.div`
  font-size: 0.82rem;
  color: #666;
  margin-bottom: 0.6rem;
`;

const InactiveTag = styled.span`
  display: inline-block;
  font-size: 0.7rem;
  font-weight: bold;
  color: #8a5a00;
  background: #fff2dd;
  padding: 1px 7px;
  border-radius: 999px;
  margin-left: 6px;
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const SmallButton = styled(Button)`
  padding: 5px 10px;
  font-size: 0.78rem;
`;

const AddCard = styled.button`
  border: 2px dashed #ccd3d9;
  border-radius: 8px;
  background: #fafbfc;
  color: #666;
  min-height: 110px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  &:hover { border-color: #005eb8; color: #005eb8; background: #f0f6fc; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(20, 30, 45, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 3rem 1rem;
  overflow-y: auto;
  z-index: 100;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 2rem;
  max-width: 680px;
  width: 100%;
`;

const ModalTitle = styled.h2`
  margin: 0 0 1.5rem;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.1rem;
  > * { flex: 1; }
`;

const Field = styled.div`
  margin-bottom: 1.1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.85rem;
  font-weight: bold;
  margin-bottom: 0.35rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 0.9rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 0.9rem;
  min-height: 70px;
  font-family: inherit;
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const ImagePreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ImagePreview = styled.div`
  width: 90px;
  height: 60px;
  border-radius: 4px;
  background: #f4f6f8 url(${p => p.$src || ''}) center/cover no-repeat;
  border: 1px solid #e0e4e8;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aab2ba;
  font-size: 0.7rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  margin: 1.75rem 0 0.75rem;
  border-top: 1px solid #eee;
  padding-top: 1.25rem;
`;

const QuestionCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e4e8;
  border-radius: 6px;
  padding: 0.9rem;
  margin-bottom: 0.75rem;
`;

const QuestionTopRow = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-bottom: 0.6rem;
  > input { flex: 2; }
  > select { flex: 1; }
`;

const QuestionBottomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: space-between;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 2rem;
  padding-top: 1.25rem;
  border-top: 1px solid #eee;
`;

const QUESTION_TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'date', label: 'Date' },
  { value: 'radio', label: 'Multiple choice (buttons)' },
  { value: 'select', label: 'Multiple choice (dropdown)' },
  { value: 'file', label: 'File upload (legacy - not yet shown to customers)' },
];
const CHOICE_QUESTION_TYPES = ['radio', 'select'];

const slugify = (str) => String(str || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');

const emptyForm = () => ({
  name: '',
  category: '',
  description: '',
  imageUrl: '',
  price: '1',
  discountPercent: '0',
  requiredDocuments: { id: true, video: true, evidence: false, scr: false, documents: false },
  questionnaire: [],
});

const EditServices = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  const api = useMemo(() => axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  useEffect(() => {
    if (!token) navigate('/');
  }, [token, navigate]);

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = adding new
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admins/services');
      setServices(res.data.services);
    } catch (err) {
      toast.error('Failed to load services.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => {
    const grouped = {};
    services.forEach((s) => {
      const cat = s.category || 'Uncategorised';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(s);
    });
    Object.values(grouped).forEach((list) => list.sort((a, b) => a.name.localeCompare(b.name)));
    return grouped;
  }, [services]);

  const categoryNames = useMemo(() => Object.keys(categories).sort(), [categories]);

  const openAdd = (presetCategory) => {
    setEditingId(null);
    setForm({ ...emptyForm(), category: presetCategory || '' });
    setModalOpen(true);
  };

  const openEdit = (service) => {
    setEditingId(service.id);
    setForm({
      name: service.name || '',
      category: service.category || '',
      description: service.description || '',
      imageUrl: service.imageUrl || '',
      price: String(service.price ?? 1),
      discountPercent: String(service.discountPercent ?? 0),
      requiredDocuments: {
        id: service.requiredDocuments?.id ?? true,
        video: service.requiredDocuments?.video ?? true,
        evidence: service.requiredDocuments?.evidence ?? false,
        scr: service.requiredDocuments?.scr ?? false,
        documents: service.requiredDocuments?.documents ?? false,
      },
      questionnaire: (service.questionnaire || []).map((q) => ({ ...q })),
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving || uploadingImage) return;
    setModalOpen(false);
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await api.post('/admins/services/upload-image', formData);
      setForm((prev) => ({ ...prev, imageUrl: res.data.url }));
      toast.success('Image uploaded.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload image.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddQuestion = () => {
    setForm((prev) => ({
      ...prev,
      questionnaire: [...prev.questionnaire, { name: '', label: '', type: 'text', required: true, options: [] }],
    }));
  };

  const handleQuestionChange = (idx, field, value) => {
    setForm((prev) => {
      const questionnaire = prev.questionnaire.map((q, i) => {
        if (i !== idx) return q;
        const updated = { ...q, [field]: value };
        if (field === 'label') {
          // Two similarly-worded questions would otherwise get the same
          // internal field name and silently overwrite each other's answer.
          const base = slugify(value);
          const takenNames = new Set(prev.questionnaire.filter((other, j) => j !== idx).map((other) => other.name));
          let candidate = base;
          let suffix = 2;
          while (takenNames.has(candidate)) {
            candidate = `${base}_${suffix++}`;
          }
          updated.name = candidate;
        }
        return updated;
      });
      return { ...prev, questionnaire };
    });
  };

  const handleRemoveQuestion = (idx) => {
    setForm((prev) => ({ ...prev, questionnaire: prev.questionnaire.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.category.trim()) {
      toast.error('A title and category are required.');
      return;
    }
    for (const q of form.questionnaire) {
      if (!q.label.trim()) {
        toast.error('Every question needs a label.');
        return;
      }
      if (CHOICE_QUESTION_TYPES.includes(q.type) && q.options.length < 2) {
        toast.error(`"${q.label}" needs at least two options for a multiple-choice question.`);
        return;
      }
    }

    setSaving(true);
    const payload = {
      name: form.name.trim(),
      category: form.category.trim(),
      description: form.description,
      imageUrl: form.imageUrl || null,
      price: Number(form.price) || 0,
      discountPercent: Number(form.discountPercent) || 0,
      requiredDocuments: form.requiredDocuments,
      questionnaire: form.questionnaire,
    };

    try {
      if (editingId) {
        const res = await api.put(`/admins/services/${editingId}`, payload);
        setServices((prev) => prev.map((s) => (s.id === editingId ? res.data.service : s)));
        toast.success('Service updated.');
      } else {
        const res = await api.post('/admins/services', payload);
        setServices((prev) => [...prev, res.data.service]);
        toast.success(`"${res.data.service.name}" added.`);
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save service.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (service) => {
    try {
      const res = await api.put(`/admins/services/${service.id}`, { active: !(service.active !== false) });
      setServices((prev) => prev.map((s) => (s.id === service.id ? res.data.service : s)));
      toast.success(res.data.service.active === false ? `"${service.name}" is now inactive.` : `"${service.name}" is now active.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update service.');
    }
  };

  const handleDelete = async (service) => {
    if (!window.confirm(`Permanently delete "${service.name}"? This can't be undone (past orders keep their own copy of the details, so this is safe to do).`)) return;
    try {
      await api.delete(`/admins/services/${service.id}`);
      setServices((prev) => prev.filter((s) => s.id !== service.id));
      toast.success(`"${service.name}" deleted.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete service.');
    }
  };

  if (loading) {
    return <Container><p>Loading services...</p></Container>;
  }

  return (
    <Container>
      <Header>
        <h1>Edit Services</h1>
        <BackLink to="/dashboard">&larr; Back to Dashboard</BackLink>
      </Header>

      {categoryNames.map((category) => (
        <CategorySection key={category}>
          <CategoryHeader>
            <CategoryTitle>{category}</CategoryTitle>
            <SmallButton onClick={() => openAdd(category)}>+ Add Service</SmallButton>
          </CategoryHeader>
          <ServiceGrid>
            {categories[category].map((service) => {
              const isActive = service.active !== false;
              return (
                <ServiceCard key={service.id} $inactive={!isActive}>
                  <Thumb $src={service.imageUrl}>{!service.imageUrl && 'No image'}</Thumb>
                  <CardBody>
                    <CardTitle>
                      {service.name}
                      {!isActive && <InactiveTag>Inactive</InactiveTag>}
                    </CardTitle>
                    <CardMeta>
                      £{Number(service.price || 0).toFixed(2)}
                      {service.discountPercent > 0 ? ` (${service.discountPercent}% off)` : ''}
                    </CardMeta>
                    <CardActions>
                      <SmallButton className="secondary" onClick={() => openEdit(service)}>Edit</SmallButton>
                      <SmallButton className="secondary" onClick={() => handleToggleActive(service)}>
                        {isActive ? 'Inactivate' : 'Activate'}
                      </SmallButton>
                      <SmallButton className="danger" onClick={() => handleDelete(service)}>Delete</SmallButton>
                    </CardActions>
                  </CardBody>
                </ServiceCard>
              );
            })}
            <AddCard onClick={() => openAdd(category)}>+ Add Service</AddCard>
          </ServiceGrid>
        </CategorySection>
      ))}

      <CategorySection>
        <CategoryHeader>
          <CategoryTitle>New Category</CategoryTitle>
        </CategoryHeader>
        <Button className="secondary" onClick={() => openAdd('')}>+ Add Service in a New Category</Button>
      </CategorySection>

      {modalOpen && (
        <ModalOverlay onClick={closeModal}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{editingId ? 'Edit Service' : 'Add Service'}</ModalTitle>

            <Field>
              <Label htmlFor="svc-name">Title</Label>
              <Input id="svc-name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </Field>

            <Field>
              <Label htmlFor="svc-category">Category</Label>
              <Input
                id="svc-category"
                list="svc-category-list"
                value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                placeholder="Pick an existing category or type a new one"
              />
              <datalist id="svc-category-list">
                {categoryNames.map((c) => <option key={c} value={c} />)}
              </datalist>
            </Field>

            <Field>
              <Label>Image</Label>
              <ImagePreviewRow>
                <ImagePreview $src={form.imageUrl}>{!form.imageUrl && 'No image'}</ImagePreview>
                <Input type="file" accept="image/*" onChange={handleImageSelect} disabled={uploadingImage} />
                {uploadingImage && <span>Uploading...</span>}
              </ImagePreviewRow>
            </Field>

            <Field>
              <Label htmlFor="svc-description">Description</Label>
              <TextArea id="svc-description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
            </Field>

            <FieldRow>
              <Field>
                <Label htmlFor="svc-price">Price (£)</Label>
                <Input id="svc-price" type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))} />
              </Field>
              <Field>
                <Label htmlFor="svc-discount">Discount (%)</Label>
                <Input id="svc-discount" type="number" min="0" max="100" step="1" value={form.discountPercent} onChange={(e) => setForm((p) => ({ ...p, discountPercent: e.target.value }))} />
              </Field>
            </FieldRow>

            <SectionTitle>Documents needed from the customer</SectionTitle>
            <CheckRow>
              <input type="checkbox" checked={form.requiredDocuments.id} onChange={(e) => setForm((p) => ({ ...p, requiredDocuments: { ...p.requiredDocuments, id: e.target.checked } }))} />
              Photo ID
            </CheckRow>
            <CheckRow>
              <input type="checkbox" checked={form.requiredDocuments.video} onChange={(e) => setForm((p) => ({ ...p, requiredDocuments: { ...p.requiredDocuments, video: e.target.checked } }))} />
              Video statement
            </CheckRow>
            <CheckRow>
              <input type="checkbox" checked={form.requiredDocuments.scr} onChange={(e) => setForm((p) => ({ ...p, requiredDocuments: { ...p.requiredDocuments, scr: e.target.checked } }))} />
              NHS Summary Care Record (SCR)
            </CheckRow>
            <CheckRow>
              <input type="checkbox" checked={form.requiredDocuments.documents} onChange={(e) => setForm((p) => ({ ...p, requiredDocuments: { ...p.requiredDocuments, documents: e.target.checked } }))} />
              Other supporting documents
            </CheckRow>
            <CheckRow>
              <input type="checkbox" checked={form.requiredDocuments.evidence} onChange={(e) => setForm((p) => ({ ...p, requiredDocuments: { ...p.requiredDocuments, evidence: e.target.checked } }))} />
              Photo evidence (e.g. test result, hospital letter)
            </CheckRow>

            <SectionTitle>Questionnaire</SectionTitle>
            {form.questionnaire.map((q, idx) => (
              <QuestionCard key={idx}>
                <QuestionTopRow>
                  <Input placeholder="Question text" value={q.label} onChange={(e) => handleQuestionChange(idx, 'label', e.target.value)} />
                  <Select value={q.type} onChange={(e) => handleQuestionChange(idx, 'type', e.target.value)}>
                    {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </Select>
                </QuestionTopRow>
                {CHOICE_QUESTION_TYPES.includes(q.type) && (
                  <Input
                    style={{ marginBottom: '0.6rem' }}
                    placeholder="Options, separated by commas (e.g. Yes, No)"
                    value={(q.options || []).join(', ')}
                    onChange={(e) => handleQuestionChange(idx, 'options', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                  />
                )}
                {q.type === 'file' && (
                  <p style={{ fontSize: '0.8rem', color: '#8a5a00', margin: '-0.3rem 0 0.6rem' }}>
                    This question won't be shown to customers yet - consider using the "documents needed" checkboxes above instead.
                  </p>
                )}
                <QuestionBottomRow>
                  <CheckRow style={{ marginBottom: 0 }}>
                    <input type="checkbox" checked={q.required} onChange={(e) => handleQuestionChange(idx, 'required', e.target.checked)} />
                    Required
                  </CheckRow>
                  <SmallButton className="danger" onClick={() => handleRemoveQuestion(idx)}>Remove</SmallButton>
                </QuestionBottomRow>
              </QuestionCard>
            ))}
            <Button className="secondary" onClick={handleAddQuestion}>+ Add Question</Button>

            <ModalActions>
              <Button className="secondary" onClick={closeModal} disabled={saving || uploadingImage}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving || uploadingImage}>{saving ? 'Saving...' : 'Save Service'}</Button>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default EditServices;
